import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import ContentContainer from "templates/content-container/contentContainer";
import ContentContainerToolbar from "ui-components/molecules/content-container-toolbar/contentContainerToolbar";
import ReloadButton from "ui-components/reload-button/reloadButton";
import { DataTable } from "ui-components/data-table";
import LabeledTextInput from "ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import LabeledSelectInput from "ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import { LabelModes } from "ui-components/molecules/labeled-inputs/labeledInput";
import XGSErrorMessage from "ui-components/error-message/errorMessage";
import Loading from "ui-components/loading/loading";
import { XGSSelectOption } from "ui-components/xgs-select/xgsSelect";
import { PAGE_IDS } from "app/data/common/pageIds";
import { AuthAuditEventType } from "app/data/dock-scan/models";
import { userSelector } from "slices/user/userSlice";
import { useDockAuthEventsInfinite } from "./hooks/useDockScanQueries";
import { useDockUsersRefresh } from "./hooks/useDockUsersRefresh";
import {
  DOCK_AUTH_AUDIT_LIST_COLUMN_OPTIONS,
  getDockAuthAuditListColumns,
} from "./dockAuthAuditListColumns";
import { DOCK_SELECT_MENU_PORTAL } from "./selectMenuPortal";
import "features/shipment-reconciliation/revenue-insights/accessorialRevenueInsights.scss";
import "./dockUsers.scss";

const EVENT_OPTIONS: XGSSelectOption[] = [
  { value: "", label: "All Event Types" },
  { value: "LOGIN_SUCCESS", label: "Login OK" },
  { value: "LOGIN_DENIED_INVALID_ID", label: "Invalid ID" },
  { value: "LOGIN_DENIED_INACTIVE", label: "Inactive" },
  { value: "LOGOUT", label: "Logout" },
  { value: "ASSIGNMENT_CREATED", label: "Assign Created" },
  { value: "ASSIGNMENT_CLOSED", label: "Assign Closed" },
  { value: "ASSIGNMENT_DENIED", label: "Assign Denied" },
];

const PAGE_SIZE = 50;

const DockAuthAuditList: React.FC = () => {
  const userState = useSelector(userSelector);
  const location = useLocation();
  const terminalNumber = userState.activeTerminal?.id
    ? Number(userState.activeTerminal.id)
    : undefined;
  const terminalName = userState.activeTerminal?.name || "Terminal";
  const terminalAirportCode = userState.activeTerminal?.shortName;

  const employeeIdFromUrl = useMemo(
    () => new URLSearchParams(location.search).get("employeeId") || undefined,
    [location.search]
  );

  const [search, setSearch] = useState("");
  const [eventFilter, setEventFilter] = useState<XGSSelectOption>(EVENT_OPTIONS[0]);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() =>
    DOCK_AUTH_AUDIT_LIST_COLUMN_OPTIONS.reduce(
      (acc, { id }) => ({ ...acc, [id]: true }),
      {} as Record<string, boolean>
    )
  );

  const {
    refreshAll,
    isRefreshing,
    lastRefreshedLabel,
    setLastRefreshedAt,
  } = useDockUsersRefresh(terminalNumber);

  const query = useMemo(
    () => ({
      take: PAGE_SIZE,
      employeeId: employeeIdFromUrl,
      eventType: (eventFilter.value || undefined) as AuthAuditEventType | undefined,
    }),
    [employeeIdFromUrl, eventFilter.value]
  );

  const {
    data,
    isLoading,
    isError,
    error,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useDockAuthEventsInfinite(query, terminalNumber != null);

  useEffect(() => {
    if (terminalNumber != null && data) {
      setLastRefreshedAt(Date.now());
    }
  }, [terminalNumber, data, setLastRefreshedAt]);

  const rows = useMemo(
    () => data?.pages.flatMap((page) => page?.items ?? []) ?? [],
    [data]
  );

  const filteredItems = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((row) => {
      const name = [row.employee?.firstName, row.employee?.lastName]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const meta = row.metadata
        ? Object.entries(row.metadata)
            .map(([k, v]) => `${k}=${v}`)
            .join(" ")
            .toLowerCase()
        : "";
      return (
        row.employeeId?.toLowerCase().includes(q) ||
        name.includes(q) ||
        row.equipmentId?.toLowerCase().includes(q) ||
        row.message?.toLowerCase().includes(q) ||
        row.eventType?.toLowerCase().includes(q) ||
        meta.includes(q)
      );
    });
  }, [rows, search]);

  const columns = useMemo(
    () =>
      getDockAuthAuditListColumns({
        terminalNumber,
        terminalName,
        terminalAirportCode,
      }),
    [terminalNumber, terminalName, terminalAirportCode]
  );

  if (!terminalNumber) {
    return (
      <ContentContainer
        className="xgs-list xgs-list--full-width"
        mods={{ "full-width": true }}
        titleComponent={
          <ContentContainerToolbar
            pageId={PAGE_IDS.DOCK_AUTH_AUDIT}
            title="Auth Audit"
            mods={{ "full-width": true }}
          />
        }
      >
        <div className="dock-users__empty">Select a terminal to view auth audit.</div>
      </ContentContainer>
    );
  }

  return (
    <ContentContainer
      className="xgs-list xgs-list--full-width"
      mods={{ "full-width": true }}
      titleComponent={
        <ContentContainerToolbar
          pageId={PAGE_IDS.DOCK_AUTH_AUDIT}
          title={`Auth Audit — ${terminalName}`}
          mods={{ "full-width": true }}
        >
          <div className="dock-users__refresh-toolbar">
            <span className="dock-users__last-refreshed">
              Last refreshed {lastRefreshedLabel ?? "—"}
            </span>
            <ReloadButton
              title="Refresh"
              spinner={isRefreshing || (isFetching && !isFetchingNextPage)}
              disabled={isRefreshing || (isFetching && !isFetchingNextPage)}
              onClick={() => void refreshAll()}
            />
            <span className="dock-users__record-count">
              Showing {filteredItems.length} event{filteredItems.length === 1 ? "" : "s"}
              {hasNextPage ? " · more available" : ""}
              {employeeIdFromUrl ? ` · employee ${employeeIdFromUrl}` : ""}
            </span>
          </div>
        </ContentContainerToolbar>
      }
    >
      <div className="dock-users__toolbar">
        <div className="dock-users__filters">
          <div className="dock-users__filter-field dock-users__filter-field--search">
            <LabeledTextInput
              labelMode={LabelModes.column}
              label="Search"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              placeholder="Employee ID, name, equipment..."
            />
          </div>
          <div className="dock-users__filter-field dock-users__filter-field--select">
            <LabeledSelectInput
              labelMode={LabelModes.column}
              label="Event Type"
              options={EVENT_OPTIONS}
              value={eventFilter}
              onValueChange={(opt) => setEventFilter(opt || EVENT_OPTIONS[0])}
              {...DOCK_SELECT_MENU_PORTAL}
            />
          </div>
        </div>
      </div>

      {isError && (
        <XGSErrorMessage>{(error as Error)?.message || "Unable to load auth audit."}</XGSErrorMessage>
      )}
      {isLoading && <Loading isLoading />}

      {!isLoading && !isError && (
        <DataTable
          columns={columns}
          data={filteredItems}
          isLoading={isFetching && !isFetchingNextPage}
          rowHeight={56}
          responsive
          stickyHeaders
          columnSelectorOptions={DOCK_AUTH_AUDIT_LIST_COLUMN_OPTIONS}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={setColumnVisibility}
          showColumnSelector
          infiniteScroll
          infiniteScrollLoading={isFetchingNextPage}
          infiniteScrollHasNext={!!hasNextPage}
          onInfiniteScroll={() => fetchNextPage()}
        />
      )}
    </ContentContainer>
  );
};

export default DockAuthAuditList;
